import { createTheme, responsiveFontSizes } from "@material-ui/core/styles"
import { amber, blue, green } from "@material-ui/core/colors"

import NovecentowideNormal from "./assets/Novecentowide-Normal.otf"
import NovecentowideBold from "./assets/Novecentowide-Bold.otf"

const NovecentowideNormalFont = {
  fontFamily: "Novecentowide",
  fontStyle: "normal",
  fontDisplay: "swap",
  fontWeight: "600",
  src: `local('Novecentowide'), local('Novecentowide-Normal'), url(${NovecentowideNormal})`,
  unicodeRange:
    "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF"
}

const NovecentowideBoldFont = {
  fontFamily: "Novecentowide",
  fontStyle: "bold",
  fontDisplay: "swap",
  fontWeight: "600",
  src: `local('Novecentowide'), local('Novecentowide-Bold'), url(${NovecentowideBold})`,
  unicodeRange:
    "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF"
}

/**
 * Defines colors variables for Auris AM / Public pages
 */
export const colors = {
  // secondary
  hoverOrange: "#ba4e02", // hover
  orangeyRed: "#ff3131", // error message
  blue: "#003296",
  darkBlue: "#212c54", // primary
  hoverDarkBlue: "#161e3a",
  white: "#fff",
  white2: "#fcfcfc",
  white3: "#fbfbfb", // background color of "Les caracteristiques du fonds"
  white4: "#f2f2f2", // used by highcharts
  lightgray: "#efefef",
  grey: "#8a8b9d",
  greyBlue: "#3a4457"
}

/**
 * Configure Material-UI theme options for Auris AM / Public pages
 * In comment, I kept some original CSS from app.zeplin.io that where defines by web agency (aka: Markentive)
 *
 * To get more details, on how to modify this file, see links below:
 * - https://material-ui.com/customization/theming/
 * - https://material-ui.com/customization/default-theme/#explore
 *
 */
const appTheme = {
  palette: {
    common: {
      black: colors.black,
      white: colors.white
    },
    background: {
      default: colors.white
    },
    primary: {
      light: colors.darkBlue,
      main: colors.darkBlue,
      dark: colors.hoverDarkBlue,
      contrastText: colors.white
    },
    secondary: {
      light: colors.darkBlue,
      main: colors.darkBlue,
      dark: colors.darkBlue,
      contrastText: colors.white
    },
    error: {
      light: colors.darkBlue,
      main: colors.darkBlue,
      dark: colors.darkBlue
    },
    warning: {
      light: amber[100],
      main: amber[400]
    },
    // ** W A R N I N G **
    // success & info are used by @material-ui/lab/Alert
    success: {
      main: green[200],
      dark: green[500]
    },
    info: {
      main: blue[200]
    },
    text: {
      // this is mainly use as variant for typography
      primary: colors.white,
      secondary: colors.greyBlue
    }
  },
  typography: {
    // Use the system font instead of the default Roboto font.
    fontFamily: ["Open Sans", "sans-serif"].join(","),
    h1: {
      // CSS from Zeplin
      // .Wording-H1 {
      //   width: 380px;
      //   height: 30px;
      //   margin: 14px 20px 42px 0;
      //   padding: 0 0 0 1px;
      //   opacity: 0;
      //   font-family: Novecentowide;
      //   font-size: 30px;
      //   font-weight: bold;
      //   font-stretch: normal;
      //   font-style: normal;
      //   line-height: 1;
      //   letter-spacing: normal;
      //   color: #000000;
      // }
      fontFamily: ["Novecentowide", "Open Sans", "sans-serif"].join(","),
      fontWeight: "bold",
      fontSize: "30px",
      lineHeight: 1,
      letterSpacing: "normal"
    },
    h2: {
      // CSS from Zeplin
      // .Wording-H2 {
      //   width: 380px;
      //   height: 30px;
      //   opacity: 0;
      //   font-family: Novecentowide;
      //   font-size: 24px;
      //   font-weight: bold;
      //   font-stretch: normal;
      //   font-style: normal;
      //   line-height: 1.25;
      //   letter-spacing: normal;
      //   color: #000000;
      // }
      fontFamily: ["Novecentowide", "Open Sans", "sans-serif"].join(","),
      fontWeight: "bold",
      fontSize: "24px",
      lineHeight: 1.25,
      letterSpacing: "normal"
    },
    h3: {
      // CSS from Zeplin
      // .Wording-H3 {
      //   width: 380px;
      //   height: 20px;
      //   opacity: 0;
      //   font-family: Novecentowide;
      //   font-size: 18px;
      //   font-weight: bold;
      //   font-stretch: normal;
      //   font-style: normal;
      //   line-height: 1.11;
      //   letter-spacing: normal;
      //   color: #000000;
      // }
      fontFamily: ["Novecentowide", "Open Sans", "sans-serif"].join(","),
      fontWeight: "bold",
      fontSize: "18px",
      lineHeight: 1.11,
      letterSpacing: "normal"
    },
    h4: {
      // CSS from Zeplin
      // .Wording-H4 {
      //   width: 380px;
      //   height: 16px;
      //   margin: 35px 240px 41px 20px;
      //   opacity: 0;
      //   font-family: Novecentowide;
      //   font-size: 14px;
      //   font-weight: bold;
      //   font-stretch: normal;
      //   font-style: normal;
      //   line-height: 1.14;
      //   letter-spacing: normal;
      //   color: #000000;
      // }
      fontFamily: ["Novecentowide", "Open Sans", "sans-serif"].join(","),
      fontWeight: "bold",
      fontSize: "14px",
      lineHeight: 1.11,
      letterSpacing: "normal"
    },
    h5: {
      // CSS from zeplin
      // .Wording-H5 {
      //   width: 380px;
      //   height: 14px;
      //   margin: 32px 20px 23px 0;
      //   opacity: 0;
      //   font-family: Novecentowide;
      //   font-size: 12px;
      //   font-weight: bold;
      //   font-stretch: normal;
      //   font-style: normal;
      //   line-height: 1.17;
      //   letter-spacing: normal;
      //   color: #000000;
      // }
      fontFamily: ["Novecentowide", "Open Sans", "sans-serif"].join(","),
      fontWeight: "bold",
      fontSize: "12px",
      lineHeight: 1.17,
      letterSpacing: "normal"
    },
    body1: {
      // CSS from Zeplin
      // .Wording-Medium-Text {
      //   width: 380px;
      //   height: 25px;
      //   margin: 14px 20px 33px 0;
      //   padding: 1px 0 0;
      //   opacity: 0;
      //   font-family: Open Sans;
      //   font-size: 15px;
      //   font-weight: normal;
      //   font-stretch: normal;
      //   font-style: normal;
      //   line-height: 1.67;
      //   letter-spacing: normal;
      //   color: #000000;
      // }
      fontWeight: "normal",
      fontSize: "15px",
      lineHeight: 1.67,
      letterSpacing: "normal"
    }
  },
  shape: {
    borderRadius: 2
  },
  overrides: {
    MuiCssBaseline: {
      "@global": {
        "@font-face": [NovecentowideNormalFont, NovecentowideBoldFont]
      }
    },
    MuiButtonBase: {
      root: {
        padding: "6px 32px",
        margin: "6px"
      }
    },
    MuiButton: {
      root: {
        borderRadius: "5px",
        fontFamily: ["Open Sans", "sans-serif"].join(","),
        fontSize: "14px",
        padding: "6px 32px"
      },
      outlined: {
        padding: "6px 32px"
      },
      contained: {
        padding: "6px 32px"
      }
    },
    MuiAlert: {
      standardInfo: {
        backgroundColor: "transparent"
      }
    }
  }
}

export const tableTheme = createTheme({
  ...appTheme,
  typography: { ...appTheme.typography, useNextVariants: true },

  overrides: {
    MUIDataTable: {
      paper: {
        borderRadius: appTheme.shape.borderRadius,
        boxSizing: "border-box",
        overflow: "auto"
      }
    },
    // table header
    MUIDataTableHeadRow: {
      root: {
        backgroundColor: appTheme.palette.secondary.main,
        color: appTheme.palette.secondary.contrastText,
        textAlign: "center",
        "& th": {
          borderRight: `2px solid ${appTheme.palette.common.white}`,
          backgroundColor: appTheme.palette.secondary.main,
          padding: "0",
          "& p": {
            padding: "22px 20px",
            fontWeight: "bold",
            textTransform: "uppercase"
          },
          "& div": {
            fontWeight: "bold",
            color: appTheme.palette.secondary.contrastText
          },
          "&:first-child": {
            borderRight: `2px solid ${appTheme.palette.secondary.main}`,
            backgroundColor: appTheme.palette.primary.main,
            color: appTheme.palette.primary.contrastText,
            textAlign: "left"
          },
          "&:last-child": {
            borderRight: "0px"
          }
        }
      }
    },
    // Text for empty table body
    MUIDataTableBody: {
      emptyTitle: {
        fontSize: "12px",
        fontStyle: "italic",
        color: appTheme.palette.text.secondary
      }
    },
    // Filters
    MUIDataTableFilterList: {
      root: {
        backgroundColor: "inherit",
        margin: "0",
        padding: "4px 6px",
        display: "flex",
        alignItems: "center",
        borderTop: `1px solid ${appTheme.palette.common.lightGray}`,

        "&:empty": {
          padding: "0"
        }
      },
      chip: {
        margin: "4px",
        height: "auto",
        padding: "2px 0",
        background: appTheme.palette.common.lightGray,
        color: appTheme.palette.primary.light,

        "&:focus": {
          color: appTheme.palette.primary.main,
          background: appTheme.palette.common.lightGray,

          "& svg": {
            fill: appTheme.palette.primary.main
          }
        },

        "& svg": {
          fill: appTheme.palette.primary.light
        }
      }
    },
    MuiTableBody: {
      root: {
        borderRadius: 5,
        borderLeft: `2px solid ${colors.lightgray}`
      }
    },
    MuiTableCell: {
      root: {
        backgroundColor: colors.lightgray,
        borderRight: "solid 2px white",
        borderBottom: "solid 2px white",
        "&:first-child": {
          width: 300,
          backgroundColor: appTheme.palette.common.white,
          borderRight: `solid 2px ${colors.lightgray}`,
          borderBottom: `solid 2px ${colors.lightgray}`
        }
      }
    },
    MuiTableRow: {
      root: {
        "&:last-child": {
          "& td": {
            borderBottom: `solid 2px ${colors.lightgray}`,
            "&:first-child": {
              borderBottomLeftRadius: 5
            },
            "&:last-child": {
              borderBottomRightRadius: 5
            }
          }
        }
      }
    }
  }
})

/**
 * Create Material-UI theme
 */
export default responsiveFontSizes(createTheme(appTheme))
